<template>
  <div
    v-if="loaded"
    class="c-collection-hero"
  >
    <div :class="collectionGridClass">
      <div
        v-if="isExpandable && !isExpanded && !isMobile"
        class="c-collection-hero__copy-mask"
      />
      <div
        id="j-copy-container-height"
        :class="copyContainerClass"
      >
        <div
          id="j-copy-height"
          class="c-collection-hero__copy"
        >
          <h1
            v-if="title"
            class="t-heading-3 u-margin-bottom-x-small"
          >
            {{ title }}
          </h1>
          <p
            v-if="subtitle"
            class="c-hero__subtitle t-heading-6 u-margin-bottom-x-small"
          >
            {{ subtitle }}
          </p>
          <wysiwyg
            v-if="description"
            :html="description"
          />
        </div>
        <btn
          v-if="isExpandable && !isMobile"
          class="c-collection-hero__read-more t-paragraph"
          type="link"
          @click="expandToggle"
        >
          {{ readMoreText }}
        </btn>
        <div
          v-if="isExpandable && !isMobile"
          class="c-collection-hero__expand-button"
          @click="expandToggle"
        >
          <OpenArrowButton
            :arrow-direction="arrowDirection"
          />
        </div>
      </div>
      <div class="c-collection-hero__image-container">
        <img
          v-if="image"
          :src="image.src"
          :alt="image.alt || title || 'Collection hero image'"
          :class="collectionImageClass"
        >
      </div>
    </div>
  </div>
  <div v-else>
    <loader />
  </div>
</template>

<script>
import Loader from "../loader/Loader";
import Wysiwyg from "../wysiwyg/Wysiwyg";
import Btn from "../button/Btn";
import OpenArrowButton from "../button/OpenArrowButton";

export default {
  components: {
    Loader,
    Wysiwyg,
    Btn,
    OpenArrowButton
  },
  props: {
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    readMore: {
      type: Boolean,
      default: true
    },
    desktopBackgroundImage: {
      type: Object,
      default: () => ({
        src: '',
        alt: 'desktopBackgroundImage'
      })
    },
    mobileBackgroundImage: {
      type: Object,
      default: () => ({
        src: '',
        alt: 'mobileBackgroundImage'
      })
    }
  },
  data() {
    return {
      image: null,
      isExpandable: false,
      isExpanded: false,
      arrowDirection: "right",
      isMobile: false,
    }
  },
  computed: {
    loaded() {
      for (var key in this.$props) {
        if (this.$props[key] !== null) {
          if (this.$props[key]['default'] !== null) {
            return true;
          }
        }
      }
      return false;
    },
    readMoreText() {
      let results = 'Read More';
      if (this.isExpanded) {
        results = 'Read Less'
      }
      return results;
    },
    collectionGridClass() {
      let results = ['c-collection-hero__grid'];
      if (this.isExpanded && !this.isMobile) {
        results.push('c-collection-hero__grid--expanded')
      }
      return results;
    },
    collectionImageClass() {
      let results = ['c-collection-hero__image']
      if (this.isExpanded && !this.isMobile) {
        results.push('c-collection-hero__image--expanded')
      }
      return results;
    },
    copyContainerClass() {
      let results = ["c-collection-hero__copy-container"]
      if (this.isExpandable) {
        results.push("c-collection-hero__copy-container--expandable")
      }
      return results;
    }
  },
  created() {
    try {
      window.addEventListener("resize", this.mobileUpdate);
      window.addEventListener("resize", this.calculateExpandable);
    } catch (err) {
      // console.log('window not loaded');
    }
  },
  mounted() {
    this.mobileUpdate();
    this.calculateExpandable();
  },
  destroyed() {
    try {
      window.removeEventListener("resize", this.mobileUpdate);
      window.removeEventListener("resize", this.calculateExpandable);
    } catch (err) {
      // console.log('window not loaded');
    }
  },
  methods: {
    expandToggle() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.arrowDirection = "left";
      } else {
        this.arrowDirection = "right";
      }
    },
    calculateExpandable() {
      // -30 for padding purposes
      let copyContainerHeight;
      let copyHeight;
      if (document.getElementById('j-copy-container-height')) {
        copyContainerHeight = document.getElementById('j-copy-container-height').clientHeight - 30;
      }
      if (document.getElementById('j-copy-height')) {
        copyHeight = document.getElementById('j-copy-height').clientHeight;
      }

      if (copyHeight > copyContainerHeight) {
        this.isExpandable = true;
      } else {
        this.isExpandable = false;
      }
    },
    mobileUpdate() {
      if (window.innerWidth <= 900) {
        this.isMobile = true;
        if (this.mobileBackgroundImage) {
          this.image = this.mobileBackgroundImage;
        }
      } else {
        this.isMobile = false;
        if (this.desktopBackgroundImage) {
          this.image = this.desktopBackgroundImage;
        }
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.c-collection-hero {
  position: relative;
  overflow: hidden;
  &__grid {
    display: grid;
    grid-template-rows: [hero-start] 21vw [hero-end];
    grid-template-columns: [copy-start] 40% [copy-end image-start] auto [image-end];

    &--expanded {
      grid-template-rows: [hero-start] auto [hero-end] ;
      grid-template-columns: [copy-start] 70% [copy-end image-start] auto [image-end];
    }

    @include respond(tab-land) {
      grid-template-rows: [hero-start] 25vw [hero-end];
    }

    @include respond(tab-port) {
      grid-template-rows: [image-start] auto [image-end copy-start] auto [copy-end];
      grid-template-columns: auto;
    }

    &--expanded {
      grid-template-rows: [hero-start] auto [hero-end] ;
      grid-template-columns: [copy-start] 70% [copy-end image-start] auto [image-end];
    }
  }
  &__copy-container {
    position: relative;
    padding-left: 6vw;
    padding-top: rem(34px);
    padding-bottom: rem(50px);
    padding-right: rem(35px);
    grid-column: copy-start / copy-end;
    display: flex;
    align-items: center;
    @include respond(tab-port) {
      grid-row: copy-start / copy-end;
    }

    &--expandable {
      display: block;
    }
  }
  &__copy-mask {
    position: absolute;
    height: 100%;
    width: 100%;
    background: linear-gradient(to top, white 12%, transparent 60%, transparent);
    z-index: -1;
  }
  &__read-more {
    position: absolute;
    z-index: 1;
    bottom: .5vw;
    text-transform: uppercase;
    @include themify($themes) {
      color: themed('foreground');
    }
  }

  &__expand-button {
    position: absolute;
    z-index: 1;
    right: 0;
    top: 50%;
    transform: translateX(50%) translateY(-50%);
  }
  &__copy {
    position: relative;
    z-index: -2;
  }
  &__image-container {
    grid-column: image-start / image-end;
    @include respond(tab-port) {
      grid-row: image-start / image-end;
    }
    @include respond(tab-port) {
      height: 400px;
      position: relative;
    }
  }

  &__image {
    width: 100%;
    @include respond(tab-land) {
      height: 100%;
      width: auto;

    }
    @include respond(tab-port) {
      height: 100%;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
    &--expanded {
      height: 100%;
      width: auto;
    }
  }

}
</style>
